import React, { useEffect, useState } from 'react';
import { Linking, Modal, Pressable, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Controller, useForm } from 'react-hook-form';
import { CheckBox, Divider } from 'react-native-elements';
import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import { Text } from '@/components/Text';
import {
  addPatient,
  fetchPatient,
  updatePatient,
  updatePatientSettings,
} from '@/api/services/patient';
import { fetchPatientsList } from '@/api/services/user';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { AvatarList } from '@/components/AvatarList';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';
import { Input } from '@/components/ui/therapist/Input';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import Card from '@/components/ui/therapist/Card';
import { Button } from '@/components/ui/therapist/Button';
import { Links } from '@/config/links';
import colors, { appTheme } from '@/config/colors';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { logError } from '@/utils/logHandling';

import { replacedPatientProfileHeader } from '@/utils/stringHelpers';
import { validatePatientCode } from '@/utils/stringValidator';
import { styles } from './styles';

type AddPatientFormData = {
  firstName: string;
  lastName: string;
  signedReleaseFromConfidentiality: boolean;
  avatar: string;
  biography: string;
  gender: string;
  diagnosis: string;
  code: string;
};

const maxInputWidth = 400;

export default function AddPatient({
  route,
  navigation,
}: StackScreenProps<TherapistStackParamList, 'AddPatientScreen'>) {
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<AddPatientFormData>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      signedReleaseFromConfidentiality: false,
      avatar: 'modules/patients/client/img/avatar/avatar1.png',
      biography: '',
      gender: '',
      diagnosis: '',
      code: '',
    },
  });
  const { showNotification } = useNotification();
  const avatar = watch('avatar');

  const [showModalToggle, setShowModalToggle] = useState(false);
  const patientId = route?.params?._id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: AddPatientFormData) => {
    try {
      setIsSubmitting(true);
      let p;
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        if (patientId) {
          p = await updatePatient(
            {
              avatar: [data.avatar],
              biography: data.biography,
              diagnosis: data.diagnosis,
              firstName: data.firstName,
              gender: data.gender,
              lastName: data.lastName,
              signedReleaseFromConfidentiality:
                data.signedReleaseFromConfidentiality,
              _id: patientId,
            },
            patientId,
          );
          const codeValid = await updatePatientSettings(
            { code: data.code },
            patientId,
          );
          if (data.code && !codeValid) {
            showNotification({
              type: 'error',
              title: 'Patient:innencode konnte nicht gespeichert werden.',
            });
          }
        } else {
          const { code, ...createData } = data;
          p = await addPatient({
            ...createData,
            avatar: [data.avatar],
          });
          if (code) {
            const codeValid = await updatePatientSettings({ code }, p.data._id);
            if (!codeValid) {
              showNotification({
                type: 'error',
                title: 'Patient:innencode konnte nicht gespeichert werden.',
              });
            }
          }
        }
        const patients = await fetchPatientsList();
        showNotification({
          type: 'success',
          title: 'Profil wurde erfolgreich gespeichert',
        });
        navigation.navigate('PatientProfile', {
          id: p.data._id,
          patients,
          replacedTitle: replacedPatientProfileHeader({
            patients: undefined,
            patientId: p.data._id,
            patient: p.data,
          }),
        });
      } else {
        setShowModalToggle(true);
      }
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      logError('Could not update patient', error);
    }
  };

  useEffect(() => {
    const findPatient = async () => {
      if (patientId) {
        try {
          const patient = await fetchPatient(patientId);
          let {
            firstName,
            lastName,
            gender,
            diagnosis,
            biography,
            avatar,
            code,
            signedReleaseFromConfidentiality,
          } = patient;

          setValue('firstName', firstName);
          setValue('lastName', lastName);
          setValue('gender', gender);
          setValue('avatar', avatar[0]);
          setValue('biography', biography);
          setValue('diagnosis', diagnosis);
          setValue('code', code ?? '');
          setValue(
            'signedReleaseFromConfidentiality',
            signedReleaseFromConfidentiality,
          );

          trigger();
        } catch (error) {
          logError('Could not fetch patient', error);
        }
      }
    };
    findPatient();
  }, []);

  useEffect(() => {
    navigation.setOptions({
      title: patientId ? 'Profil bearbeiten' : 'Profil hinzufügen',
      headerRight: () => (
        <Button label="Abbrechen" icon="close" onPress={navigation.goBack} />
      ),
    });
  }, []);

  return (
    <BodyWrapper showSidebar={false} bodyStyle={{ width: '80%', flex: 1 }}>
      <KeyboardAwareScrollView
        enableOnAndroid
        extraHeight={useResponsiveHeight(30)}
        extraScrollHeight={30}
      >
        <Card styleBody={{ padding: 0 }} style={styles.cardStyle}>
          <View style={styles.section}>
            <View>
              <Text style={styles.fieldLabel}>Patient:in Profilname</Text>
            </View>
            <View style={styles.nameRow}>
              <View style={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="firstName"
                  rules={{
                    required: 'Bitte geben Sie den Vornamen an',
                  }}
                  render={({ field }) => (
                    <Input
                      label="Vorname"
                      required={true}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        trigger('firstName');
                      }}
                      status={
                        errors.firstName
                          ? 'warning'
                          : field.value.length > 0
                            ? 'validated'
                            : 'default'
                      }
                      statusMessage={errors.firstName?.message}
                    />
                  )}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="lastName"
                  rules={{
                    required: 'Bitte geben Sie den Nachnamen an',
                  }}
                  render={({ field }) => (
                    <Input
                      label="Nachname"
                      required={true}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        trigger('lastName');
                      }}
                      status={
                        errors.lastName
                          ? 'warning'
                          : field.value.length > 0
                            ? 'validated'
                            : 'default'
                      }
                      statusMessage={errors.lastName?.message}
                    />
                  )}
                />
              </View>
            </View>
          </View>
          <Divider color={colors.newUI.therapist.grey20} width={1} />
          <View style={styles.section}>
            <AvatarList
              defaultAvatar={avatar}
              onAvatarPress={(avatar: string) => setValue('avatar', avatar)}
            />
          </View>
        </Card>
        <Card styleBody={styles.section} style={styles.cardStyle}>
          <View>
            <Text style={styles.fieldLabel}>Patienten-Code</Text>
            <Text style={styles.fieldInfo}>
              Der Patienten-Code wird für die Zuweisung von Eigentrainings
              benötigt. Sie können den Code später nachtragen.
            </Text>
            <Controller
              control={control}
              name="code"
              rules={{
                validate: (v) => !v || validatePatientCode(v),
              }}
              render={({ field }) => (
                <Input
                  style={{ maxWidth: maxInputWidth }}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                    trigger('code');
                  }}
                  onBlur={field.onBlur}
                  status={
                    errors.code
                      ? 'warning'
                      : field.value.length > 0
                        ? 'validated'
                        : 'default'
                  }
                  statusMessage={
                    errors.code ? 'Ungültiger Patient:innencode' : undefined
                  }
                />
              )}
            />
          </View>
          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <Input
                  label="Geschlecht"
                  style={{ maxWidth: maxInputWidth }}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => {
                    field.onBlur();
                    trigger('gender');
                  }}
                  status={
                    errors.gender
                      ? 'warning'
                      : field.value
                        ? 'validated'
                        : undefined
                  }
                />
              )}
            />
          </View>

          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="diagnosis"
              render={({ field }) => (
                <Input
                  label="Diagnose"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  status={field.value.length > 0 ? 'validated' : 'default'}
                />
              )}
            />
          </View>
          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="biography"
              render={({ field }) => {
                const charCount = field.value.length;
                return (
                  <Input
                    label="Notizen"
                    placeholder="(optional, max. 150 Zeichen)"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    status={charCount > 0 ? 'validated' : 'default'}
                    additionalInfo={`${charCount}/150`}
                    multiline
                    numberOfLines={3}
                    maxLength={150}
                  />
                );
              }}
            />
          </View>
        </Card>
        <View style={styles.footer}>
          {!patientId && (
            <Controller
              control={control}
              name="signedReleaseFromConfidentiality"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <CheckBox
                  onPress={() => {
                    field.onChange(!field.value);
                  }}
                  checked={field.value}
                  containerStyle={{
                    backgroundColor: undefined,
                    borderWidth: 0,
                  }}
                  checkedColor={appTheme.primary}
                  title={
                    <Text style={{ marginLeft: 8 }}>
                      <Text style={styles.redText}>*</Text>
                      Ich habe zur Kenntnis genommen, dass bei der Speicherung
                      eines Patienten-Profils unter Klarnamen eine {'\n'}
                      <Text
                        onPress={() =>
                          Linking.openURL(Links.confidentiality.href)
                        }
                        style={{ color: colors.link }}
                      >
                        Schweigepflichtsentbindung
                      </Text>
                      <Text> erforderlich ist. </Text>
                    </Text>
                  }
                />
              )}
            />
          )}
          <View style={styles.footerButtons}>
            <Button
              look="tertiary"
              onPress={() => navigation.goBack()}
              label="Abbrechen"
              disabled={isSubmitting}
            />
            <Button
              icon="addUser"
              look="primary"
              onPress={handleSubmit(onSubmit)}
              label="Patient:in hinzufügen"
              disabled={!isValid || isSubmitting}
            />
          </View>
        </View>
      </KeyboardAwareScrollView>

      <Modal
        supportedOrientations={[
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
        animationType="none"
        transparent={true}
        visible={showModalToggle}
        onRequestClose={() => {
          setShowModalToggle(false);
        }}
      >
        <View style={styles.centeredView}>
          <View style={styles.modalView}>
            <Text style={styles.modalTitle}>
              Keine Verbindung zum neolexon Server
            </Text>
            <View style={styles.underline} />
            <Text style={styles.modalText}>
              Bitte überprüfen Sie Ihre Internetverbindung und versuchen es
              erneut.
            </Text>
            <Pressable
              style={styles.button}
              onPress={() => setShowModalToggle(false)}
            >
              <Text style={styles.modalButton}>OK</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </BodyWrapper>
  );
}
